import { axios, APIBASE_REVIVERAUTHENTICATION, APIBASE_REVIVERUSERS } from "./ApiConfig";
import { User, UserCompanies, RedirectionTypeEnumValues } from "@/types";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { saveUserToken } from "@/utils/sessionStorage";

export const getLoginUrl = (email: string, origin: string, port?: any) =>
  axios.get<{
    type: RedirectionTypeEnumValues;
    url: string;
  }>(`${APIBASE_REVIVERAUTHENTICATION}api/authentication/loginUrl`, {
    params: {
      email,
      origin,
      port,
    },
  });

export const getExternalToken = async (authProviderValue: string, port?: number | null) => {
  if (!authProviderValue) return Promise.reject(new Error("authProviderValue is required"));
  const res = await axios.post(`${APIBASE_REVIVERAUTHENTICATION}api/authentication/login/${authProviderValue}`, null, {
    params: {
      port,
    },
    headers: {
      "Content-Type": "application/json",
    },
  });
  const headers = res.headers;
  const token = headers["authorization"];
  if (token) {
    saveUserToken(token);
  }
  return res;
};

export const useGetExternalToken = (
  code: string,
  port?: number | null,
  queryOptions?: Partial<UseQueryOptions<User>>
) => {
  return useQuery<User>({
    queryKey: ["getExternalToken"],
    queryFn: async () => {
      const result = await getExternalToken(code, port);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getUserById = async (id: number) => axios.get(`${APIBASE_REVIVERUSERS}api/user/get/${id}`);

export const useGetUserById = (id: number, options?: Partial<UseQueryOptions<User>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<User>({
    queryKey: ["getUserById", id],
    queryFn: async () => {
      const result = await getUserById(id);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getCompaniesByUserId = async (id: number, companyId: number) => {
  const res = await axios.get<UserCompanies>(`${APIBASE_REVIVERUSERS}api/user/${id}/companies`, {
    headers: {
      UserId: id,
      CompanyId: companyId,
    },
  });
  return res;
};

export const useGetCompaniesByUserId = (
  id: number,
  companyId: number,
  options?: Partial<UseQueryOptions<UserCompanies>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<UserCompanies>({
    queryKey: ["getCompaniesByUserId", id, companyId],
    queryFn: async () => {
      const result = await getCompaniesByUserId(id, companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const logOut = () => axios.post(`${APIBASE_REVIVERAUTHENTICATION}api/authentication/logout`);

export const getLogoutUrl = (port: string) =>
  axios.get(`${APIBASE_REVIVERAUTHENTICATION}api/authentication/logoutUrl`, {
    params: {
      port,
    },
  });

export const postApacheUserToken = async (uuid: string, language: string) => {
  const urlParams: Record<string, any> = { lang: language };
  return axios.post(`${APIBASE_REVIVERAUTHENTICATION}api/superset/guest-token`, {
    dashboardId: uuid,
    urlParams,
  });
};
